import { createSelector } from "reselect";
import moment from "moment-timezone";

import { APPLICATION_STATE, MISMATCH_TYPE, REJECTION_REASON } from "lib/constants";

export const getInviteApplications = createSelector(
    (state) => state.applications,
    (applications) => {
        const apps = Object.keys(applications)
            .map((k) => applications[k])
            .filter(
                (a) =>
                    a.state === APPLICATION_STATE.INTERVIEW && a.time_interview_schedule === null,
            );
        // Sort by apps that don't need slots (i.e. user isn't waiting on anything)
        // then by time app was approved for interview
        apps.sort((a, b) => {
            if (!a.needed_slots && b.needed_slots) {
                return -1;
            }

            if (a.needed_slots && !b.needed_slots) {
                return 1;
            }

            return +new Date(a.time_interview) > +new Date(b.time_interview) ? -1 : 1;
        });
        return apps;
    },
);

export const getExpireApplications = createSelector(
    (state) => state.applications,
    (applications) => {
        const oneWeekAgo = moment().subtract(7, "days");
        const apps = Object.keys(applications)
            .map((k) => applications[k])
            .filter(
                (a) =>
                    a.state === APPLICATION_STATE.REJECTED &&
                    a.reason === REJECTION_REASON.EXPIRED &&
                    moment(a.time_rejected).isAfter(oneWeekAgo) &&
                    a.position.active &&
                    a.time_reapplied === null,
            );
        apps.sort((a, b) => (+new Date(a.time_rejected) > +new Date(b.time_rejected) ? -1 : 1));
        return apps;
    },
);

export const getInterviewStateApplications = createSelector(
    (state) => state.applications,
    (applications) => {
        const apps = Object.keys(applications)
            .map((k) => applications[k])
            .filter(
                (a) =>
                    a.state === APPLICATION_STATE.INTERVIEW &&
                    a.time_interview_schedule !== null &&
                    moment(a.time_interview_schedule).isAfter(),
            );
        apps.sort((a, b) =>
            +new Date(a.time_interview_schedule) < +new Date(b.time_interview_schedule) ? -1 : 1,
        );
        return apps;
    },
);

export const filterHardFails = createSelector(
    (applications) => applications,
    (applications) =>
        applications.filter(
            (a) => !(a.mismatch_type && a.sms_regretted && a.state === APPLICATION_STATE.APPLIED),
        ),
);

const softFailTypes = [MISMATCH_TYPE.COMMUTE, MISMATCH_TYPE.AVAILABILITY];
export const filterSoftFails = createSelector(
    (applications) => applications,
    (applications) =>
        applications.filter(
            (a) =>
                !(
                    softFailTypes.indexOf(a.mismatch_type) > -1 &&
                    a.mismatch_ack === null &&
                    !a.sms_regretted &&
                    a.state === APPLICATION_STATE.APPLIED
                ),
        ),
);

export const getRecentApplications = createSelector(
    (state) => state.applications,
    (applications) => {
        const threeMonthAgo = moment().subtract(90, "days");
        const apps = filterSoftFails(Object.keys(applications).map((k) => applications[k]))
            .filter((a) => a.time_created && moment(a.time_created).isAfter(threeMonthAgo))
            .filter(
                (a) =>
                    (a.state === APPLICATION_STATE.APPLIED && !a.sms_regretted) ||
                    a.state === APPLICATION_STATE.HIRE_PENDING ||
                    (a.state === APPLICATION_STATE.INTERVIEW &&
                        moment(a.time_interview_schedule).isBefore()) ||
                    a.sms_regretted ||
                    a.state === APPLICATION_STATE.CANCELED ||
                    a.state === APPLICATION_STATE.STARTED,
            );
        apps.sort((a, b) => {
            if (
                a.state === APPLICATION_STATE.HIRE_PENDING &&
                b.state === APPLICATION_STATE.HIRE_PENDING
            ) {
                return new Date(a.time_pending) < new Date(b.time_pending) ? -1 : 1;
            }
            if (a.state === APPLICATION_STATE.HIRE_PENDING) {
                return -1;
            }
            if (b.state === APPLICATION_STATE.HIRE_PENDING) {
                return 1;
            }
            return new Date(a.time_created) < new Date(b.time_created) ? -1 : 1;
        });
        return apps;
    },
);

export const getAllApps = createSelector(
    (state) => state.applications,
    (applications) => Object.keys(applications).map((k) => applications[k]),
);

export const getIncompleteApplications = createSelector(
    (state) => state.applications,
    (applications) => {
        const twoWeeksAgo = moment().subtract(14, "days");
        return Object.keys(applications)
            .map((k) => applications[k])
            .filter(
                (a) =>
                    a.time_created &&
                    moment(a.time_created).isAfter(twoWeeksAgo) &&
                    a.state === APPLICATION_STATE.STARTED,
            );
    },
);
